/**
 * Built-in functions of the expression language (W1-10c core-expr; contract src/core/expr/types.ts).
 *
 *   abs min max clamp round floor ceil sqrt pow exp log sin cos tan asin acos atan atan2 sign
 *   lerp(a, b, u)  ease(idOrName, u)  alt(a, b, ...)  rand(a, b)  randn(mu, sigma)  snap(v, step)
 *   beat(sec)  sec(beat)
 * plus two curve helpers so every model ease has an expression form: `ease(id, u, left, right)` (part of the
 * curve, as the easing picker's slice) and `bezier(x1, y1, x2, y2, u)` (custom curve).
 *
 * Conventions: angles in radians (a `deg` value given to sin/cos/tan is converted); `log(x)` is the natural
 * logarithm, `log(x, base)` any base; `round` rounds halves away from zero, `round(x, digits)` to decimals
 * (negative digits round to tens, hundreds, ...); `%` and `alt` use the floored remainder (never negative for a
 * positive divisor); `min`/`max` take one or more values; `clamp(x, lo, hi)` accepts lo > hi.
 * `ease` uses Phira's curve formulas (core/ease, f32) with u clamped to [0, 1]; Linear returns u exactly.
 * `rand()` is uniform in [0, 1), `rand(r)` in [-r, r), `rand(a, b)` in [a, b); `randn()` is a standard normal,
 * `randn(sigma)` and `randn(mu, sigma)` scaled (Box-Muller). Random draws come from the Rng passed to
 * `Expr.apply` in evaluation order (left to right, items in time order), so a seed reproduces every value.
 * `alt(a, b, ...)` evaluates only the argument it picks: number (i mod k).
 */

import { Ease } from '../ease';
import type { Ease as EaseValue } from '../ease/types';
import type { Rng } from '../math/types';
import type { TempoMap } from '../time/types';
import { EXPR_MSG } from './errors';

export type FnName =
  | 'abs'
  | 'min'
  | 'max'
  | 'clamp'
  | 'round'
  | 'floor'
  | 'ceil'
  | 'sqrt'
  | 'pow'
  | 'exp'
  | 'log'
  | 'sin'
  | 'cos'
  | 'tan'
  | 'asin'
  | 'acos'
  | 'atan'
  | 'atan2'
  | 'sign'
  | 'lerp'
  | 'ease'
  | 'bezier'
  | 'alt'
  | 'rand'
  | 'randn'
  | 'snap'
  | 'beat'
  | 'sec';

/**
 * How a function treats the units of its arguments (see check.ts):
 *   numeric  every argument becomes a plain number (`50%` -> 0.5; `deg` -> radians when `trig`); result plain
 *   same     the first `count` arguments (all when omitted) share one unit, which the result keeps
 *            (`min(10%, 20%)` stays percent, `lerp(0deg, 90deg, u)` stays degrees); later arguments are numeric
 *   any      arguments of any unit; result plain (sign)
 *   ease     first argument: curve number or name; the rest numeric
 *   toBeat   one time in seconds -> beats;  toSec: one beat -> seconds
 */
export type ArgPolicy =
  | { readonly kind: 'numeric'; readonly trig?: true }
  | { readonly kind: 'same'; readonly count?: number }
  | { readonly kind: 'any' }
  | { readonly kind: 'ease' }
  | { readonly kind: 'toBeat' }
  | { readonly kind: 'toSec' };

export interface FnContext {
  readonly rng: Rng;
  readonly tempo: TempoMap | undefined;
  /** Throws a run-time error underlining the call. */
  fail(key: string, params?: Readonly<Record<string, string | number>>): never;
}

export type FnImpl = (a: readonly number[], c: FnContext) => number;

export interface FnSpec {
  readonly name: FnName;
  readonly min: number;
  /** Maximum argument count (Infinity = any number). */
  readonly max: number;
  /** Parameter list for signatures ("a, b, u"). */
  readonly params: string;
  readonly policy: ArgPolicy;
  /** Help text key (namespace 'expr'). */
  readonly desc: string;
  /** Example call (code, not translated). */
  readonly example: string;
  readonly usesRand: boolean;
  readonly usesTempo: boolean;
  /** Null for `alt`, which the evaluator handles lazily. */
  readonly impl: FnImpl | null;
}

/** Argument k (arity is checked before evaluation; NaN keeps the types honest). */
function at(a: readonly number[], k: number): number {
  return a[k] ?? Number.NaN;
}

/** Rounds halves away from zero (2.5 -> 3, -2.5 -> -3). */
export function roundHalfAway(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

/** Number of decimals in the shortest representation of `x` (0.25 -> 2, 1e-7 -> 7). */
function decimalsOf(x: number): number {
  const s = String(Math.abs(x));
  const exp = /e-(\d+)$/.exec(s);
  if (exp !== null) {
    const mantissa = s.slice(0, s.indexOf('e'));
    const dot = mantissa.indexOf('.');
    return Number(exp[1]) + (dot < 0 ? 0 : mantissa.length - dot - 1);
  }
  const dot = s.indexOf('.');
  return dot < 0 ? 0 : s.length - dot - 1;
}

/** x * 10^k by moving the decimal point in the shortest representation, so typed decimals stay exact. */
function shiftDecimal(x: number, k: number): number {
  const [mantissa = '0', exponent = '0'] = String(x).split('e');
  return Number(`${mantissa}e${String(Number(exponent) + k)}`);
}

/**
 * Rounds to `digits` decimals (negative: tens, hundreds, ...). The decimal point is moved in the decimal text,
 * not by multiplying, so round(1.005, 2) = 1.01 (1.005 * 100 is 100.49999999999999 in binary).
 */
function roundTo(x: number, digits: number): number {
  const shifted = shiftDecimal(x, digits);
  // Beyond the range of doubles x has no digits at this scale left to round.
  if (!Number.isFinite(shifted)) return x;
  return shiftDecimal(roundHalfAway(shifted), -digits);
}

/** Smallest (or largest) argument; a loop rather than Math.min(...a), which overflows on huge calls. */
function extreme(a: readonly number[], pickMax: boolean): number {
  let out = pickMax ? -Infinity : Infinity;
  for (const x of a) {
    if (Number.isNaN(x)) return Number.NaN;
    if (pickMax ? x > out : x < out) out = x;
  }
  return out;
}

function snap(v: number, step: number): number {
  if (step === 0) return v;
  const s = Math.abs(step);
  const k = roundHalfAway(v / s);
  const out = k * s;
  const dec = decimalsOf(s);
  // Remove binary noise (snap(0.7, 0.1) = 0.7, not 0.7000000000000001).
  return dec <= 15 ? Number(out.toFixed(dec)) : out;
}

function clamp01(u: number): number {
  return u < 0 ? 0 : u > 1 ? 1 : u;
}

function easeY(a: readonly number[], c: FnContext): number {
  const id = at(a, 0);
  const u = clamp01(at(a, 1));
  if (!Number.isInteger(id) || id < 1 || id > 29) c.fail(EXPR_MSG.easingRuntime, { id: String(id) });
  const left = a.length > 2 ? at(a, 2) : 0;
  const right = a.length > 3 ? at(a, 3) : 1;
  const e: EaseValue = { kind: 'tween', id, left, right };
  const reason = Ease.validateFor(e, 'general');
  if (reason !== null) c.fail(reason.key, reason.params);
  // Phira's linear curve is the identity; returning u itself keeps ramps free of f32 noise.
  if (id === 1) return u;
  return Ease.modelY(e, u);
}

function bezierY(a: readonly number[], c: FnContext): number {
  const e: EaseValue = { kind: 'bezier', p: [at(a, 0), at(a, 1), at(a, 2), at(a, 3)] };
  const reason = Ease.validateFor(e, 'general');
  if (reason !== null) c.fail(reason.key, reason.params);
  return Ease.bezierY(e.p, clamp01(at(a, 4)));
}

function rand(a: readonly number[], c: FnContext): number {
  const r = c.rng.next();
  if (a.length === 0) return r;
  const lo = a.length === 1 ? -at(a, 0) : at(a, 0);
  const hi = a.length === 1 ? at(a, 0) : at(a, 1);
  return lo + (hi - lo) * r;
}

function randn(a: readonly number[], c: FnContext): number {
  // Box-Muller; 1 - next() is in (0, 1], so the logarithm is finite.
  const u1 = 1 - c.rng.next();
  const u2 = c.rng.next();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  if (a.length === 0) return z;
  if (a.length === 1) return at(a, 0) * z;
  return at(a, 0) + at(a, 1) * z;
}

function needTempo(c: FnContext, name: FnName): TempoMap {
  if (c.tempo === undefined) return c.fail(EXPR_MSG.needsTempo, { name });
  return c.tempo;
}

type SpecInput = Omit<FnSpec, 'desc' | 'usesRand' | 'usesTempo'> & {
  readonly usesRand?: true;
  readonly usesTempo?: true;
};

function spec(s: SpecInput): FnSpec {
  return { ...s, desc: `expr.fn.${s.name}`, usesRand: s.usesRand === true, usesTempo: s.usesTempo === true };
}

const NUMERIC = { kind: 'numeric' } as const;
const TRIG = { kind: 'numeric', trig: true } as const;
const SAME = { kind: 'same' } as const;

export const FUNCTIONS: Readonly<Record<FnName, FnSpec>> = {
  abs: spec({
    name: 'abs',
    min: 1,
    max: 1,
    params: 'x',
    policy: SAME,
    example: 'abs(v)',
    impl: (a) => Math.abs(at(a, 0)),
  }),
  min: spec({
    name: 'min',
    min: 1,
    max: Infinity,
    params: 'a, b, ...',
    policy: SAME,
    example: 'min(v, 300)',
    impl: (a) => extreme(a, false),
  }),
  max: spec({
    name: 'max',
    min: 1,
    max: Infinity,
    params: 'a, b, ...',
    policy: SAME,
    example: 'max(v, -300)',
    impl: (a) => extreme(a, true),
  }),
  clamp: spec({
    name: 'clamp',
    min: 3,
    max: 3,
    params: 'x, lo, hi',
    policy: SAME,
    example: 'clamp(v * 2, -675, 675)',
    impl: (a) => {
      const lo = Math.min(at(a, 1), at(a, 2));
      const hi = Math.max(at(a, 1), at(a, 2));
      return Math.min(Math.max(at(a, 0), lo), hi);
    },
  }),
  round: spec({
    name: 'round',
    min: 1,
    max: 2,
    params: 'x, digits',
    policy: { kind: 'same', count: 1 },
    example: 'round(v, 1)',
    impl: (a, c) => {
      if (a.length < 2) return roundHalfAway(at(a, 0));
      const d = at(a, 1);
      if (!Number.isInteger(d) || d < -15 || d > 15) c.fail(EXPR_MSG.roundDigits);
      return roundTo(at(a, 0), d);
    },
  }),
  floor: spec({
    name: 'floor',
    min: 1,
    max: 1,
    params: 'x',
    policy: SAME,
    example: 'floor(v / 10) * 10',
    impl: (a) => Math.floor(at(a, 0)),
  }),
  ceil: spec({
    name: 'ceil',
    min: 1,
    max: 1,
    params: 'x',
    policy: SAME,
    example: 'ceil(v)',
    impl: (a) => Math.ceil(at(a, 0)),
  }),
  sqrt: spec({
    name: 'sqrt',
    min: 1,
    max: 1,
    params: 'x',
    policy: NUMERIC,
    example: 'sqrt(2)',
    impl: (a) => Math.sqrt(at(a, 0)),
  }),
  pow: spec({
    name: 'pow',
    min: 2,
    max: 2,
    params: 'x, y',
    policy: NUMERIC,
    example: 'pow(u, 2)',
    impl: (a) => at(a, 0) ** at(a, 1),
  }),
  exp: spec({
    name: 'exp',
    min: 1,
    max: 1,
    params: 'x',
    policy: NUMERIC,
    example: 'exp(-u)',
    impl: (a) => Math.exp(at(a, 0)),
  }),
  log: spec({
    name: 'log',
    min: 1,
    max: 2,
    params: 'x, base',
    policy: NUMERIC,
    example: 'log(v, 10)',
    impl: (a) => {
      const x = at(a, 0);
      if (!(x > 0)) return Number.NaN;
      if (a.length < 2) return Math.log(x);
      const b = at(a, 1);
      if (!(b > 0) || b === 1) return Number.NaN;
      return Math.log(x) / Math.log(b);
    },
  }),
  sin: spec({
    name: 'sin',
    min: 1,
    max: 1,
    params: 'x',
    policy: TRIG,
    example: 'sin(u * pi)',
    impl: (a) => Math.sin(at(a, 0)),
  }),
  cos: spec({
    name: 'cos',
    min: 1,
    max: 1,
    params: 'x',
    policy: TRIG,
    example: 'cos(30deg)',
    impl: (a) => Math.cos(at(a, 0)),
  }),
  tan: spec({
    name: 'tan',
    min: 1,
    max: 1,
    params: 'x',
    policy: TRIG,
    example: 'tan(u)',
    impl: (a) => Math.tan(at(a, 0)),
  }),
  asin: spec({
    name: 'asin',
    min: 1,
    max: 1,
    params: 'x',
    policy: NUMERIC,
    example: 'asin(0.5)',
    impl: (a) => Math.asin(at(a, 0)),
  }),
  acos: spec({
    name: 'acos',
    min: 1,
    max: 1,
    params: 'x',
    policy: NUMERIC,
    example: 'acos(0.5)',
    impl: (a) => Math.acos(at(a, 0)),
  }),
  atan: spec({
    name: 'atan',
    min: 1,
    max: 1,
    params: 'x',
    policy: NUMERIC,
    example: 'atan(1)',
    impl: (a) => Math.atan(at(a, 0)),
  }),
  atan2: spec({
    name: 'atan2',
    min: 2,
    max: 2,
    params: 'y, x',
    policy: NUMERIC,
    example: 'atan2(1, 1)',
    impl: (a) => Math.atan2(at(a, 0), at(a, 1)),
  }),
  sign: spec({
    name: 'sign',
    min: 1,
    max: 1,
    params: 'x',
    policy: { kind: 'any' },
    example: 'sign(v)',
    impl: (a) => {
      const x = at(a, 0);
      return x > 0 ? 1 : x < 0 ? -1 : 0;
    },
  }),
  lerp: spec({
    name: 'lerp',
    min: 3,
    max: 3,
    params: 'a, b, u',
    policy: { kind: 'same', count: 2 },
    example: 'lerp(-400, 400, u)',
    impl: (a) => {
      const u = at(a, 2);
      return u === 1 ? at(a, 1) : at(a, 0) + (at(a, 1) - at(a, 0)) * u;
    },
  }),
  ease: spec({
    name: 'ease',
    min: 2,
    max: 4,
    params: 'curve, u, left, right',
    policy: { kind: 'ease' },
    example: 'lerp(0, 400, ease(outSine, u))',
    impl: easeY,
  }),
  bezier: spec({
    name: 'bezier',
    min: 5,
    max: 5,
    params: 'x1, y1, x2, y2, u',
    policy: NUMERIC,
    example: 'bezier(0.25, 0.1, 0.25, 1, u)',
    impl: bezierY,
  }),
  alt: spec({
    name: 'alt',
    min: 1,
    max: Infinity,
    params: 'a, b, ...',
    policy: SAME,
    example: 'v * alt(1, -1)',
    impl: null,
  }),
  rand: spec({
    name: 'rand',
    min: 0,
    max: 2,
    params: 'a, b',
    policy: SAME,
    example: 'v + rand(-30, 30)',
    usesRand: true,
    impl: rand,
  }),
  randn: spec({
    name: 'randn',
    min: 0,
    max: 2,
    params: 'mu, sigma',
    policy: SAME,
    example: 'randn(0, 20)',
    usesRand: true,
    impl: randn,
  }),
  snap: spec({
    name: 'snap',
    min: 2,
    max: 2,
    params: 'x, step',
    policy: SAME,
    example: 'snap(v, 50)',
    impl: (a) => snap(at(a, 0), at(a, 1)),
  }),
  beat: spec({
    name: 'beat',
    min: 1,
    max: 1,
    params: 'seconds',
    policy: { kind: 'toBeat' },
    example: 'beat(s + 0.5)',
    usesTempo: true,
    impl: (a, c) => needTempo(c, 'beat').beatF64At(at(a, 0)),
  }),
  sec: spec({
    name: 'sec',
    min: 1,
    max: 1,
    params: 'beat',
    policy: { kind: 'toSec' },
    example: 'sec(t + 1)',
    usesTempo: true,
    impl: (a, c) => needTempo(c, 'sec').secAtBeatF64(at(a, 0)),
  }),
};

export const FUNCTION_NAMES: readonly FnName[] = Object.keys(FUNCTIONS) as FnName[];

export function findFunction(name: string): FnSpec | undefined {
  const n = name.toLowerCase();
  return Object.hasOwn(FUNCTIONS, n) ? FUNCTIONS[n as FnName] : undefined;
}

export function signatureOf(f: FnSpec): string {
  return `${f.name}(${f.params})`;
}
